/** Pure template-string renderers for the API-tokens section.
 *
 * Layer: `view` per docs/design/spa-account-extraction-260524.md §3.2.
 *
 * Covers:
 *   - renderApiTokensPlaceholder  — initial loading shell
 *   - renderApiTokensEmpty        — empty-state (no tokens yet)
 *   - renderApiTokensTable        — populated token list
 *   - renderGenerateModal         — generate-new-token dialog
 *   - renderRevealModal           — one-time plaintext reveal dialog
 *   - renderDeleteModal           — delete-confirmation dialog
 *   - renderRevokeAllModal        — revoke-all-tokens dialog
 *
 * Purity contract: every exported function accepts typed data and returns a
 * string. No DOM access, no fetch, no window.X mutations.
 *
 * Extracted from web/static/account.html in Phase AC.4.
 * Implements docs/design/account-api-tokens-260510.md.
 */
import { fmtDate } from './view-usage.js';
// ─── Placeholder (loading shell) ─────────────────────────────────────────
export function renderApiTokensPlaceholder() {
    return `<section class="account-section" data-testid="api-tokens" aria-labelledby="api-tokens-h">
        <h2 id="api-tokens-h">API Tokens <span class="section-count" id="api-tokens-count">Loading…</span></h2>
        <div id="api-tokens-body">
            <p style="font-size:12px;color:var(--text-muted)">Loading…</p>
        </div>
    </section>`;
}
// ─── Empty state ──────────────────────────────────────────────────────────
export function renderApiTokensEmpty() {
    return `<div class="empty-state">
        <span class="empty-icon" aria-hidden="true">&#128273;</span>
        <h3>No API tokens yet</h3>
        <p>
            API tokens let you script remediation jobs from your laptop or CI/CD pipeline.
            Use them with <code>Authorization: Bearer ada_&hellip;</code>.
        </p>
        <button class="account-btn primary" type="button"
                data-action="api-token-generate"
                aria-haspopup="dialog" aria-label="Generate new API token">
            + Generate new API token
        </button>
    </div>`;
}
export function renderApiTokensTable(esc, tokens) {
    const rows = tokens.map(t => {
        const status = t.revoked_at ? 'revoked'
            : t.is_expired ? 'expired'
                : 'active';
        const badge = status === 'active' ? '<span class="token-badge active">Active</span>'
            : status === 'expired' ? '<span class="token-badge expired">Expired</span>'
                : '<span class="token-badge revoked">Revoked</span>';
        const expires = t.expires_at
            ? esc(fmtDate(t.expires_at))
            : '<span class="token-never">Never</span>';
        const lastUsed = t.last_used_at ? esc(fmtDate(t.last_used_at)) : 'Never';
        const canDelete = status !== 'revoked';
        const actionBtn = canDelete
            ? `<button class="account-btn sm danger" type="button"
                data-action="api-token-delete"
                data-token-id="${esc(t.id)}"
                data-token-name="${esc(t.name)}"
                data-token-prefix="${esc(t.prefix)}"
                aria-label="Delete token '${esc(t.name)}'"
                aria-haspopup="dialog">Delete</button>`
            : '';
        return `<tr class="${esc(status)}" data-token-id="${esc(t.id)}">
            <td>
                <div class="token-name">${esc(t.name)}</div>
                <span class="token-prefix" aria-label="Token prefix ${esc(t.prefix)}">${esc(t.prefix)}</span>
            </td>
            <td class="token-date">${esc(fmtDate(t.created_at))}</td>
            <td class="token-date">${lastUsed}</td>
            <td>${expires}</td>
            <td>${badge}</td>
            <td>${actionBtn}</td>
        </tr>`;
    }).join('');
    const hasActive = tokens.some(t => !t.revoked_at && !t.is_expired);
    const revokeAllRow = hasActive ? `
        <div class="revoke-all-row">
            <p>Emergency: stolen device or compromised credentials?</p>
            <button class="account-btn danger" type="button"
                    data-action="api-token-revoke-all"
                    aria-label="Revoke all active API tokens">
                Revoke all tokens
            </button>
        </div>` : '';
    return `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;flex-wrap:wrap;gap:8px">
            <p style="font-size:13px;color:var(--text-muted);margin:0">
                Use these tokens with <code style="font-size:12px;background:#f3f4f6;padding:1px 5px;border-radius:3px">Authorization: Bearer ada_&hellip;</code>.
            </p>
            <button class="account-btn primary" type="button"
                    data-action="api-token-generate"
                    aria-haspopup="dialog" aria-label="Generate new API token">
                + Generate new token
            </button>
        </div>
        <div class="token-table-wrap" role="region" aria-label="API tokens list">
            <table class="token-table" aria-label="Your API tokens">
                <thead><tr>
                    <th scope="col">Name &amp; prefix</th>
                    <th scope="col">Created</th>
                    <th scope="col">Last used</th>
                    <th scope="col">Expires</th>
                    <th scope="col">Status</th>
                    <th scope="col"><span style="position:absolute;width:1px;height:1px;overflow:hidden">Actions</span></th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>
        </div>
        ${revokeAllRow}`;
}
// ─── Modals ───────────────────────────────────────────────────────────────
export function renderGenerateModal() {
    return `<div class="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="gen-modal-title"
                 data-token-modal="generate">
        <div class="modal-box">
            <button class="modal-close" aria-label="Close dialog" type="button"
                    data-action="api-token-modal-close">&#x2715;</button>
            <h3 id="gen-modal-title">Generate new API token</h3>
            <p class="modal-subtitle">Give this token a name that identifies where you will use it.</p>
            <form id="api-token-gen-form" onsubmit="return false">
                <div class="form-field">
                    <label for="api-token-name-input">Token name
                        <span aria-hidden="true" style="color:var(--error)">*</span></label>
                    <input type="text" id="api-token-name-input" class="form-input"
                           placeholder="e.g. Laptop CLI, GitHub Actions"
                           maxlength="64" autocomplete="off" required aria-required="true"
                           aria-describedby="api-token-name-hint">
                    <p class="field-hint" id="api-token-name-hint">Max 64 characters.</p>
                    <p class="field-error" id="api-token-gen-error" hidden></p>
                </div>
                <div class="form-field">
                    <label id="api-token-scope-label">Access</label>
                    <div class="scope-grid" role="radiogroup" aria-labelledby="api-token-scope-label">
                        <button type="button" class="scope-option selected" data-token-scope="doc-edit:apply" role="radio" aria-checked="true">Document-editing only</button>
                        <button type="button" class="scope-option" data-token-scope="account:full" role="radio" aria-checked="false">Full account access</button>
                    </div>
                    <p class="field-hint" id="api-token-scope-hint">Document-editing tokens can read and remediate documents through the editor API, but cannot access billing, uploads, or account settings.</p>
                </div>
                <div class="form-field">
                    <label id="api-token-expiry-label">Expiration</label>
                    <div class="expiry-grid" role="group" aria-labelledby="api-token-expiry-label">
                        <button type="button" class="expiry-option" data-expiry-days="0" aria-pressed="false">No expiry</button>
                        <button type="button" class="expiry-option" data-expiry-days="30" aria-pressed="false">30 days</button>
                        <button type="button" class="expiry-option selected" data-expiry-days="90" aria-pressed="true">90 days</button>
                        <button type="button" class="expiry-option" data-expiry-days="365" aria-pressed="false">1 year</button>
                    </div>
                    <p class="field-hint">Document-editing tokens default to 90 days — machine credentials should rotate. Tokens without an expiry remain valid until manually deleted.</p>
                </div>
                <div class="modal-actions">
                    <button type="button" class="account-btn" data-action="api-token-modal-close">Cancel</button>
                    <button type="submit" class="account-btn primary"
                            data-action="api-token-gen-submit">Generate token</button>
                </div>
            </form>
        </div>
    </div>`;
}
export function renderRevealModal(esc, result) {
    const created = result.created_at ? fmtDate(result.created_at) : '';
    const expiresLine = result.expires_at
        ? `Created ${esc(created)} &middot; Expires ${esc(fmtDate(result.expires_at))}`
        : `Created ${esc(created)} &middot; No expiry`;
    return `<div class="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="reveal-modal-title"
                 data-token-modal="reveal">
        <div class="modal-box">
            <h3 id="reveal-modal-title" style="color:#16a34a">
                &#10003;&nbsp; Token generated: <em>${esc(result.name)}</em>
            </h3>
            <p class="modal-subtitle">${expiresLine}</p>
            <div class="token-reveal-box" role="region" aria-label="Your new API token">
                <p style="font-size:11px;color:var(--text-muted);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px">Your API token</p>
                <code id="api-token-revealed-value" aria-label="API token value">${esc(result.plaintext)}</code>
            </div>
            <button type="button" class="copy-btn"
                    data-action="api-token-copy"
                    aria-label="Copy API token to clipboard">
                &#128203;&nbsp; Copy to clipboard
            </button>
            <div class="amber-warning" role="alert" aria-live="assertive">
                <span class="warn-icon" aria-hidden="true">&#9888;</span>
                <span>
                    <strong>This is the only time you'll see this token.</strong>
                    We hash it server-side and cannot show it again.
                    Save it now in a password manager or secrets vault &mdash; it won't be shown again.
                </span>
            </div>
            <div class="modal-actions">
                <button type="button" class="account-btn primary"
                        data-action="api-token-modal-close">
                    I've copied it &mdash; close
                </button>
            </div>
        </div>
    </div>`;
}
export function renderDeleteModal(esc, tokenId, tokenName, tokenPrefix) {
    return `<form class="modal-backdrop" role="dialog" aria-modal="true"
                  aria-labelledby="del-modal-title"
                  data-token-modal="delete" data-token-id="${esc(tokenId)}"
                  onsubmit="return false">
        <div class="modal-box">
            <button class="modal-close" aria-label="Close dialog" type="button"
                    data-action="api-token-modal-close">&#x2715;</button>
            <h3 id="del-modal-title">Delete token?</h3>
            <p class="modal-subtitle">You are about to delete the token named:</p>
            <div class="delete-emphasis" role="status">
                &#128273;&nbsp; <strong>${esc(tokenName)}</strong>
                <span style="font-size:12px;font-weight:400;margin-left:8px">(${esc(tokenPrefix)})</span>
            </div>
            <p style="font-size:14px;color:var(--text);line-height:1.6">
                Any code or scripts using this token will immediately receive
                <strong>401 Unauthorized</strong>. This action cannot be undone.
            </p>
            <div class="modal-actions">
                <button type="button" class="account-btn" data-action="api-token-modal-close">Cancel</button>
                <button type="submit" class="account-btn danger"
                        data-action="api-token-delete-confirm"
                        data-token-id="${esc(tokenId)}"
                        style="font-weight:700;padding:7px 20px">
                    Delete token
                </button>
            </div>
        </div>
    </form>`;
}
export function renderRevokeAllModal() {
    return `<form class="modal-backdrop" role="dialog" aria-modal="true"
                  aria-labelledby="rev-all-title" data-token-modal="revoke-all"
                  onsubmit="return false">
        <div class="modal-box">
            <button class="modal-close" aria-label="Close dialog" type="button"
                    data-action="api-token-modal-close">&#x2715;</button>
            <h3 id="rev-all-title" style="color:var(--error)">Revoke ALL active tokens?</h3>
            <p style="font-size:14px;color:var(--text);line-height:1.6;margin-bottom:12px">
                Every active API token for your account will be revoked immediately.
                Any running scripts will receive <strong>401 Unauthorized</strong> on the next request.
            </p>
            <p style="font-size:13px;color:var(--text-muted);margin-bottom:12px">
                Use this for stolen devices or compromised credentials. You can generate new tokens immediately.
            </p>
            <div class="modal-actions">
                <button type="button" class="account-btn" data-action="api-token-modal-close">Cancel</button>
                <button type="submit" class="account-btn danger" style="font-weight:700;padding:7px 20px"
                        data-action="api-token-revoke-all-confirm">
                    Revoke all tokens
                </button>
            </div>
        </div>
    </form>`;
}
